import { useState, useEffect } from "react";
import { Gamepad2, Music, Loader2 } from "lucide-react";

const STATUS = {
  online: { color: "#23a55a", label: "Online" },
  idle: { color: "#f0b232", label: "Idle" },
  dnd: { color: "#f23f43", label: "Do Not Disturb" },
  offline: { color: "#80848e", label: "Offline" },
};

function activityText(data) {
  const acts = data.activities || [];
  const custom = acts.find((a) => a.type === 4);
  if (custom && (custom.state || custom.emoji)) {
    const emoji = custom.emoji && !custom.emoji.id ? custom.emoji.name + " " : "";
    return { icon: null, text: `${emoji}${custom.state || ""}`.trim() };
  }
  if (data.listening_to_spotify && data.spotify) {
    return { icon: "spotify", text: `Listening to ${data.spotify.song}` };
  }
  const playing = acts.find((a) => a.type === 0);
  if (playing) return { icon: "game", text: `Playing ${playing.name}` };
  return { icon: null, text: "currently doing nothing" };
}

export default function DiscordPresence({ discordId }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await fetch(`https://api.lanyard.rest/v1/users/${discordId}`);
        const j = await r.json();
        if (!active) return;
        if (j.success) { setData(j.data); setState("ok"); }
        else setState("error");
      } catch {
        if (active) setState("error");
      }
    };
    load();
    const id = setInterval(load, 20000);
    return () => { active = false; clearInterval(id); };
  }, [discordId]);

  if (state === "loading")
    return (
      <div className="w-full rounded-2xl border border-white/10 bg-black/30 backdrop-blur-xl p-4 flex items-center gap-3" data-testid="discord-presence">
        <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" />
        <span className="text-xs text-zinc-400">Connecting to Discord…</span>
      </div>
    );

  if (state === "error")
    return (
      <div className="w-full rounded-2xl border border-white/10 bg-black/30 backdrop-blur-xl p-4 text-left" data-testid="discord-presence">
        <p className="text-xs text-zinc-500">Discord presence unavailable. Join <span className="text-indigo-300">discord.gg/lanyard</span> to enable it.</p>
      </div>
    );

  const u = data.discord_user || {};
  const status = STATUS[data.discord_status] || STATUS.offline;
  const name = u.display_name || u.global_name || u.username || "Discord User";
  const avatar = u.avatar
    ? `https://cdn.discordapp.com/avatars/${u.id}/${u.avatar}.${u.avatar.startsWith("a_") ? "gif" : "png"}?size=128`
    : "https://cdn.discordapp.com/embed/avatars/0.png";
  const act = activityText(data);

  return (
    <div className="w-full rounded-2xl border border-white/10 bg-black/30 backdrop-blur-xl p-4 flex items-center gap-3 text-left" data-testid="discord-presence">
      <div className="relative shrink-0">
        <img src={avatar} alt={name} className="w-14 h-14 rounded-2xl object-cover" />
        <span
          className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-[3px] border-black"
          style={{ backgroundColor: status.color }}
          title={status.label}
          data-testid="discord-status-dot"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-white truncate" data-testid="discord-name">{name}</span>
          <svg viewBox="0 0 24 24" className="w-4 h-4 text-indigo-400 shrink-0" fill="currentColor"><path d="M20.3 4.4A19 19 0 0 0 15.6 3l-.3.5c1.6.4 2.9 1 4.1 1.8a14 14 0 0 0-12.8 0C7.8 4.5 9.1 3.9 10.7 3.5L10.4 3a19 19 0 0 0-4.7 1.4C2.3 9 1.4 13.5 1.8 18a19 19 0 0 0 5.8 2.9l.4-.6c-1-.4-1.9-.8-2.7-1.4l.6-.4c3.6 1.7 7.5 1.7 11 0l.6.4c-.8.6-1.7 1-2.7 1.4l.4.6A19 19 0 0 0 22.2 18c.5-5.2-.8-9.7-1.9-13.6ZM8.4 15.2c-1 0-1.9-1-1.9-2.1 0-1.2.8-2.1 1.9-2.1s1.9 1 1.9 2.1c0 1.2-.8 2.1-1.9 2.1Zm7.2 0c-1 0-1.9-1-1.9-2.1 0-1.2.8-2.1 1.9-2.1s1.9 1 1.9 2.1c0 1.2-.8 2.1-1.9 2.1Z" /></svg>
        </div>
        <p className="text-xs text-zinc-400 truncate flex items-center gap-1.5" data-testid="discord-activity">
          {act.icon === "spotify" && <Music className="w-3 h-3 text-green-400" />}
          {act.icon === "game" && <Gamepad2 className="w-3 h-3 text-indigo-400" />}
          {act.text}
        </p>
      </div>
    </div>
  );
}
